import { useState } from "react";
import { useMyProfile } from "../data";
import { resyncEventsToCalendar, startGoogleContactsConnect } from "../actions";

/**
 * The one-way push of CRM events into Google Calendar.
 *
 * Its own card rather than a tick inside the contacts one, because it is a
 * different integration in the opposite direction: contacts are read from
 * Google, events are written to it. Folding a write permission into a card
 * headed "read-only, one-way" would be quietly untrue.
 *
 * ## Whose calendar
 *
 * The product owner raised this himself: "שים לב שזה משנה מי יוצר את האירוע
 * כלומר למי זה משוייך לי או לשירה." An event goes to the calendar of whoever
 * owns it in the CRM, not whoever pressed save — so the card says so, and each
 * account connects separately.
 */
export function GoogleCalendarCard() {
  const profile = useMyProfile();
  const connection = profile?.googleContacts;
  const enabled = connection?.calendarEnabled === true && connection?.status === "active";

  const [busy, setBusy] = useState<null | "connect" | "resync">(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function readableError(err: unknown): string {
    const message = (err as { message?: string })?.message;
    return message && /[֐-׿]/.test(message) ? message : "משהו השתבש. נסו שוב.";
  }

  async function connect() {
    setBusy("connect");
    setError(null);
    try {
      const { url } = await startGoogleContactsConnect(true);
      window.open(url, "_blank", "noopener,noreferrer");
      setNote("ייפתח חלון של גוגל. אשרו גם גישה ליומן וחזרו לכאן.");
    } catch (err) {
      setError(readableError(err));
    } finally {
      setBusy(null);
    }
  }

  async function resync() {
    setBusy("resync");
    setError(null);
    setNote(null);
    try {
      const r = await resyncEventsToCalendar({});
      const pushed = r.counts.pushed ?? 0;
      const skipped = Object.entries(r.counts)
        .filter(([k]) => k.startsWith("skipped"))
        .reduce((n, [, v]) => n + v, 0);
      setNote(
        pushed > 0
          ? `העברנו ${pushed} אירועים ליומן.${skipped > 0 ? ` דילגנו על ${skipped}.` : ""}`
          : "לא הועבר כלום ליומן. כנראה אין אירועים עתידיים, או שהם משויכים למישהו אחר.",
      );
    } catch (err) {
      setError(readableError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="section">
      <div className="card">
        <div className="card-title">
          יומן גוגל
          <span className={`conn-pill conn-${enabled ? "ok" : "off"}`}>
            {enabled ? "מחובר" : "לא מחובר"}
          </span>
        </div>

        <div className="card-pad">
          <p className="faint" style={{ marginTop: 0 }}>
            אירוע שנוצר כאן מופיע ביומן בטלפון. ההעברה היא בכיוון אחד בלבד, מה־CRM לגוגל.
            שינוי שתעשו ביומן גוגל לא יחזור לכאן, ובפעם הבאה שהאירוע ישתנה כאן הוא
            יידרס.
          </p>
          <p className="faint">
            האירועים נכנסים ליומן נפרד בשם „חב״ד הפארק”, לא ליומן האישי. אפשר להסתיר אותו
            או לצבוע אותו בלי לגעת בשאר.
          </p>
          <p className="faint">
            <b>לאיזה יומן:</b> לפי מי שהאירוע משויך אליו ב־CRM, לא לפי מי שיצר אותו. אירוע
            של שירה ייכנס ליומן של שירה. לכן כל אחד מכם צריך לחבר כאן את החשבון שלו, בנפרד.
          </p>

          {note && <div className="notice info">{note}</div>}
          {error && <div className="notice error">{error}</div>}

          <div className="btn-row">
            <button className="btn" onClick={connect} disabled={busy !== null}>
              {busy === "connect" ? "פותחים…" : enabled ? "חיבור מחדש" : "חיבור היומן"}
            </button>
            {enabled && (
              <button className="btn" onClick={resync} disabled={busy !== null}>
                {busy === "resync" ? "מעבירים…" : "העברת אירועים קיימים"}
              </button>
            )}
          </div>

          {enabled && (
            <p className="faint">
              אירועים חדשים עוברים ליומן לבד. „העברת אירועים קיימים” נועדה לפעם הראשונה, בשביל
              אירועים שכבר היו במערכת לפני החיבור.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
